<script setup>
import { computed } from 'vue'
import { RouterLink } from 'vue-router'

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
  canManageUsers: {
    type: Boolean,
    default: false,
  },
  canViewBatches: {
    type: Boolean,
    default: false,
  },
  isSuperAdmin: {
    type: Boolean,
    default: false,
  },
  userDisplayName: {
    type: String,
    default: 'Account',
  },
  activeOrgId: {
    type: [String, Number, null],
    default: null,
  },
  activeSubOrgId: {
    type: [String, Number, null],
    default: null,
  },
  organizations: {
    type: Array,
    default: () => [],
  },
  subOrgs: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['close', 'logout', 'update:activeOrgId', 'update:activeSubOrgId'])

const handleNavClick = () => {
  emit('close')
}

const handleLogout = () => {
  emit('close')
  emit('logout')
}

const activeOrgIdModel = computed({
  get: () => (props.activeOrgId === null ? '' : String(props.activeOrgId)),
  set: (value) => emit('update:activeOrgId', value ? Number(value) : null),
})

const activeSubOrgIdModel = computed({
  get: () => (props.activeSubOrgId === null ? '' : String(props.activeSubOrgId)),
  set: (value) => emit('update:activeSubOrgId', value ? Number(value) : null),
})

const filteredSubOrgs = computed(() => {
  const activeSubOrgs = props.subOrgs.filter((item) => item?.is_deleted !== true)
  if (!props.activeOrgId) return activeSubOrgs
  return activeSubOrgs.filter((s) => Number(s.organization_id) === Number(props.activeOrgId))
})
</script>

<template>
  <!-- Backdrop -->
  <transition name="mobile-menu-backdrop">
    <div
      v-if="isOpen"
      class="mobile-menu-backdrop mobile-only-surface"
      @click="emit('close')"
    />
  </transition>

  <!-- Slide-in drawer -->
  <transition name="mobile-menu-slide">
    <nav v-if="isOpen" class="mobile-menu mobile-only-surface" role="navigation" aria-label="Mobile navigation">
      <div class="mobile-menu-header">
        <span class="mobile-menu-title">Menu</span>
        <button class="mobile-menu-close" type="button" aria-label="Close menu" @click="emit('close')">
          ✕
        </button>
      </div>

      <!-- Navigation links -->
      <div class="mobile-menu-links">
        <RouterLink to="/dashboard" @click="handleNavClick">Dashboard</RouterLink>
        <RouterLink to="/lotteries" @click="handleNavClick">Lotteries</RouterLink>
        <RouterLink v-if="canViewBatches" to="/seller" @click="handleNavClick">Seller</RouterLink>
        <RouterLink v-if="canManageUsers" to="/users" @click="handleNavClick">Users</RouterLink>
        <RouterLink v-if="isSuperAdmin" to="/admin/orgs" @click="handleNavClick">Organizations</RouterLink>
        <RouterLink v-if="isSuperAdmin" to="/admin/suborgs" @click="handleNavClick">SubOrgs</RouterLink>
        <RouterLink v-if="isSuperAdmin" to="/admin/smtp" @click="handleNavClick">SMTP</RouterLink>
        <RouterLink v-if="isSuperAdmin" to="/admin/audit-logs" @click="handleNavClick">Audit Logs</RouterLink>
        <RouterLink v-if="isSuperAdmin" to="/admin/operations" @click="handleNavClick">Operations</RouterLink>
        <RouterLink v-if="isSuperAdmin || canManageUsers" to="/admin/financial-overview" @click="handleNavClick">Financial Overview</RouterLink>
      </div>

      <div class="mobile-menu-account-section">
        <p class="mobile-menu-section-title">{{ userDisplayName }}</p>
        <div class="mobile-menu-links mobile-menu-account-links">
          <RouterLink to="/settings/profile" @click="handleNavClick">Profile</RouterLink>
          <RouterLink to="/settings/security" @click="handleNavClick">Security</RouterLink>
          <button class="mobile-menu-action" type="button" @click="handleLogout">Logout</button>
        </div>
      </div>

      <!-- Org controls (show in mobile menu instead of topbar) -->
      <div class="mobile-menu-org-section">
        <label class="mobile-menu-label">
          Organization
          <select v-model="activeOrgIdModel" class="mobile-menu-select">
            <option value="">Select organization</option>
            <option v-for="org in organizations" :key="org.id" :value="String(org.id)">
              {{ org.name }}
            </option>
          </select>
        </label>

        <label class="mobile-menu-label">
          Sub-organization
          <select
            v-model="activeSubOrgIdModel"
            class="mobile-menu-select"
            :disabled="!activeOrgId || !filteredSubOrgs.length"
          >
            <option value="">Select sub-organization</option>
            <option v-for="subOrg in filteredSubOrgs" :key="subOrg.id" :value="String(subOrg.id)">
              {{ subOrg.name }}
            </option>
          </select>
        </label>
      </div>
    </nav>
  </transition>
</template>

<style scoped>
.mobile-only-surface {
  display: none;
}

@media (max-width: 768px) {
  .mobile-only-surface.mobile-menu-backdrop {
    display: block;
  }

  .mobile-only-surface.mobile-menu {
    display: flex;
  }
}

/* Backdrop */
.mobile-menu-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 200;
}

/* Drawer */
.mobile-menu {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: min(320px, 85vw);
  background: #fff;
  z-index: 210;
  display: flex;
  flex-direction: column;
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.18);
  overflow-y: auto;
}

/* Header row */
.mobile-menu-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
  min-height: 60px;
}

.mobile-menu-title {
  font-weight: 700;
  font-size: 1.05rem;
  color: #111827;
}

.mobile-menu-close {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 1.1rem;
  color: #6b7280;
  border-radius: 8px;
  min-height: unset;
  padding: 0;
}

.mobile-menu-close:hover {
  background: #f3f4f6;
}

/* Navigation links */
.mobile-menu-links {
  display: flex;
  flex-direction: column;
  padding: 0.75rem 0.5rem;
}

.mobile-menu-links a {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  color: #111827;
  font-weight: 600;
  border-radius: 8px;
  font-size: 1rem;
  min-height: 44px;
  transition: background 0.12s;
}

.mobile-menu-links a:hover,
.mobile-menu-links a.router-link-active {
  background: #eff6ff;
  color: #2563eb;
}

.mobile-menu-account-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem 0.5rem 0;
  border-top: 1px solid #e5e7eb;
}

.mobile-menu-section-title {
  padding: 0 1rem;
  font-size: 0.8rem;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.mobile-menu-account-links {
  padding-top: 0;
}

.mobile-menu-action {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 44px;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #b91c1c;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
}

.mobile-menu-action:hover {
  background: #fef2f2;
}

/* Org section */
.mobile-menu-org-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  border-top: 1px solid #e5e7eb;
  margin-top: auto;
}

.mobile-menu-label {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  font-size: 0.85rem;
  color: #374151;
  font-weight: 500;
}

.mobile-menu-select {
  width: 100%;
  padding: 0.5rem 0.6rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  color: #111827;
  font-size: 0.95rem;
  min-height: 44px;
}

.mobile-menu-select:disabled {
  color: #9ca3af;
  background: #f9fafb;
}

/* Backdrop transition */
.mobile-menu-backdrop-enter-active,
.mobile-menu-backdrop-leave-active {
  transition: opacity 0.22s ease;
}

.mobile-menu-backdrop-enter-from,
.mobile-menu-backdrop-leave-to {
  opacity: 0;
}

/* Drawer slide transition */
.mobile-menu-slide-enter-active,
.mobile-menu-slide-leave-active {
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.mobile-menu-slide-enter-from,
.mobile-menu-slide-leave-to {
  transform: translateX(-100%);
}
</style>

